import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import M3Card from '../../M3Card';
import ToolResult from './ToolResult';
import { SITES_QUERY, TAGS_QUERY } from '../../../graphql/queries';
import {
  UPDATE_PUBLICATION_SITE_MUTATION,
  UPDATE_PUBLICATION_GRAVE_TAGS_MUTATION
} from '../../../graphql/tools';

function toggle(list, id) {
  return list.includes(id) ? list.filter((entry) => entry !== id) : [...list, id];
}

/** Bulk organization: assign one site or a set of tags to every grave. */
export default function OrganizationTools({ publicationId }) {
  const [siteId, setSiteId] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [siteResult, setSiteResult] = useState(null);
  const [tagsResult, setTagsResult] = useState(null);
  const [siteBusy, setSiteBusy] = useState(false);
  const [tagsBusy, setTagsBusy] = useState(false);

  const [updateSite] = useMutation(UPDATE_PUBLICATION_SITE_MUTATION);
  const [updateGraveTags] = useMutation(UPDATE_PUBLICATION_GRAVE_TAGS_MUTATION);
  const { data: sitesData, loading: sitesLoading } = useQuery(SITES_QUERY);
  const { data: tagsData, loading: tagsLoading } = useQuery(TAGS_QUERY);

  if (sitesLoading || tagsLoading) return null;
  const sites = (sitesData && sitesData.sites) || [];
  const tags = (tagsData && tagsData.tags) || [];

  const handleSite = () => {
    setSiteBusy(true);
    setSiteResult(null);
    updateSite({ variables: { id: publicationId, site: siteId } })
      .then((res) =>
        setSiteResult({ ok: true, count: res.data.updatePublicationSite.updatedCount })
      )
      .catch((e) => setSiteResult({ ok: false, message: e.message }))
      .finally(() => setSiteBusy(false));
  };

  const handleTags = () => {
    setTagsBusy(true);
    setTagsResult(null);
    updateGraveTags({ variables: { id: publicationId, tags: selectedTags } })
      .then((res) =>
        setTagsResult({ ok: true, count: res.data.updatePublicationGraveTags.updatedCount })
      )
      .catch((e) => setTagsResult({ ok: false, message: e.message }))
      .finally(() => setTagsBusy(false));
  };

  return (
    <>
      <M3Card variant="outlined">
        <M3Card.Header title="Assign site" subtitle="Set the same site for every grave" />
        <M3Card.Body>
          <div className="tool-controls">
            <label className="tool-field">
              Site
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">Select a site…</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSite}
              disabled={siteBusy || !siteId}
            >
              {siteBusy ? 'Updating…' : 'Assign to all graves'}
            </button>
            <ToolResult result={siteResult} />
          </div>
        </M3Card.Body>
      </M3Card>

      <M3Card variant="outlined">
        <M3Card.Header title="Assign tags" subtitle="Tag every grave in the publication" />
        <M3Card.Body>
          <div className="tool-controls">
            <div className="tool-field">
              <span>Tags</span>
              <div className="tool-tag-picker">
                {tags.map((tag) => (
                  <label
                    key={tag.id}
                    className={`tool-tag-option ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.id)}
                      onChange={() => setSelectedTags(toggle(selectedTags, tag.id))}
                    />
                    {tag.name}
                  </label>
                ))}
                {tags.length === 0 && <span className="text-muted">No tags defined.</span>}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleTags}
              disabled={tagsBusy || selectedTags.length === 0}
            >
              {tagsBusy ? 'Updating…' : 'Apply to all graves'}
            </button>
            <ToolResult result={tagsResult} />
          </div>
        </M3Card.Body>
      </M3Card>
    </>
  );
}
